import {Injectable} from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import {ApiBaseService} from './api-base-service';
import {SearchRequest} from './requests/search-request';
import {Observable} from 'rxjs';
import {ApiResponse, ListResponse, SuccessList} from './responses/api-response';
import {PartyEventResponse} from './responses/PartyEvents/party-event-response';
import {CreatePartyEventRequest} from './requests/party-events/create-party-event-request';
import {UpdatePartyEventRequest} from './requests/party-events/update-party-event-request';
import {PartyEventSummaryResponse} from './responses/PartyEvents/party-event-summary-response';
import {Guid} from 'guid-typescript';
import {PartyEventTopResponse} from '@portal/api/responses/PartyEvents/party-event-top-response';
import {PartyEventReportSettingsRequest} from "@portal/api/requests/party-events/party-event-report-settings-request";
import {UpdatePartyEventRegistrationFormRequest} from "@portal/api/requests/party-events/update-party-event-registration-form-request";
import {PartyEventRegistrationFormResponse} from "@portal/api/responses/PartyEvents/party-event-registration-form-response";
import {StorageService} from "@portal/shared/services/storage.service";
import {PartySelectedService} from "@portal/shared/services/party-selected.service";
import {OrganizationType} from "@portal/api/enums/organization-type";
import {ActivityRegistrationPriceResponse} from '@portal/api/contracts/activity-registration-prices/activity-registration-price-response';
import {PartyEventPublicResponse} from '@portal/api/responses/PartyEvents/party-event-public-response';

@Injectable({
    providedIn: 'root'
})
export class PartyEventsService extends ApiBaseService {

    private selectedEvent: PartyEventResponse;

    constructor(
        public http: HttpClient,
        private _storageService: StorageService,
        private _partySelectedService: PartySelectedService) {
        super(http);
        this.controller = 'v1/events';
    }

    add(obj: CreatePartyEventRequest): Observable<ApiResponse<string>> {
        const orgId = this.getOrgId();
        const partyId = this.getPartyId();
        if (this.isOrgScope() || !partyId) {
            return this.post<ApiResponse<string>, CreatePartyEventRequest>(obj, `v1/orgs/${orgId}/events/add`);
        }
        return this.post<ApiResponse<string>, CreatePartyEventRequest>(obj, `v1/orgs/${orgId}/parties/${partyId}/events/add`);
    }

    update(obj: UpdatePartyEventRequest): Observable<ApiResponse<string>> {
        const orgId = this.getOrgId();
        const partyId = this.getPartyId();
        if (this.isOrgScope() || !partyId) {
            return this.put<ApiResponse<string>, UpdatePartyEventRequest>(obj, `v1/orgs/${orgId}/events/update`);
        }
        return this.put<ApiResponse<string>, UpdatePartyEventRequest>(obj, `v1/orgs/${orgId}/parties/${partyId}/events/update`);
    }

    getById(partyEventId: string): Observable<ApiResponse<PartyEventResponse>> {
        const orgId = this.getOrgId();
        const partyId = this.getPartyId();
        if (this.isOrgScope() || !partyId) {
            return this.get<ApiResponse<PartyEventResponse>>(`v1/orgs/${orgId}/events/${partyEventId}`);
        }
        return this.get<ApiResponse<PartyEventResponse>>(`v1/orgs/${orgId}/parties/${partyId}/events/${partyEventId}`);
    }

    getPublicById(partyEventId: string): Observable<ApiResponse<PartyEventPublicResponse>> {
        return this.get<ApiResponse<PartyEventPublicResponse>>(this.controller + `/public/${partyEventId}`);
    }

    getFormEdit(partyEventId: string): Observable<ApiResponse<UpdatePartyEventRequest>> {
        const orgId = this.getOrgId();
        const partyId = this.getPartyId();
        if (this.isOrgScope() || !partyId) {
            return this.get<ApiResponse<UpdatePartyEventRequest>>(`v1/orgs/${orgId}/events/form/edit/${partyEventId}`);
        }
        return this.get<ApiResponse<UpdatePartyEventRequest>>(`v1/orgs/${orgId}/parties/${partyId}/events/form/edit/${partyEventId}`);
    }

    deleteEvent(id: string): Observable<ApiResponse<boolean>> {
        const orgId = this.getOrgId();
        const partyId = this.getPartyId();
        if (this.isOrgScope() || !partyId) {
            return this.deleteBase<ApiResponse<boolean>>(`v1/orgs/${orgId}/events/${id}`);
        }
        return this.deleteBase<ApiResponse<boolean>>(`v1/orgs/${orgId}/parties/${partyId}/events/${id}`);
    }

    listByParty(obj: SearchRequest, partyId: Guid = null): Observable<ApiResponse<ListResponse<PartyEventResponse[]>>> {
        const orgId = this.getOrgId();
        const selectedPartyId = this.getPartyId() || partyId;
        if (this.isOrgScope() || !selectedPartyId) {
            return this.post<ApiResponse<ListResponse<PartyEventResponse[]>>, SearchRequest>(obj, `v1/orgs/${orgId}/events/listall`);
        }
        return this.post<ApiResponse<ListResponse<PartyEventResponse[]>>, SearchRequest>(obj, `v1/orgs/${orgId}/parties/${selectedPartyId}/events/listall`);
    }

    getSummary(id: Guid): Observable<ApiResponse<PartyEventSummaryResponse>> {
        const orgId = this.getOrgId();
        const partyId = this.getPartyId();
        if (this.isOrgScope() || !partyId) {
            return this.get<ApiResponse<PartyEventSummaryResponse>>(`v1/orgs/${orgId}/events/summary/${id}`);
        }
        return this.get<ApiResponse<PartyEventSummaryResponse>>(`v1/orgs/${orgId}/parties/${partyId}/events/summary/${id}`);
    }

    summary(partyId: Guid = null): Observable<ApiResponse<PartyEventTopResponse>> {
        const orgId = this.getOrgId();
        const selectedPartyId = this.getPartyId() || partyId;
        if (this.isOrgScope() || !selectedPartyId) {
            return this.get<ApiResponse<PartyEventTopResponse>>(`v1/orgs/${orgId}/events/top`);
        }
        return this.get<ApiResponse<PartyEventTopResponse>>(`v1/orgs/${orgId}/parties/${selectedPartyId}/events/top`);
    }

    downloadFile(request: PartyEventReportSettingsRequest): Observable<HttpResponse<Blob>> {
        const orgId = this.getOrgId();
        const partyId = this.getPartyId();
        if (this.isOrgScope() || !partyId) {
            return this.postFile(`v1/orgs/${orgId}/events/download-report`, request);
        }
        return this.postFile(`v1/orgs/${orgId}/parties/${partyId}/events/download-report`, request);
    }

    getCurrentEvent() {
        if(this.selectedEvent){
            return this.selectedEvent;
        }

        this.selectedEvent = JSON.parse(localStorage.getItem('currentEvent'));
        return this.selectedEvent;
    }

    setCurrentEvent(partyEvent: PartyEventResponse) {
        localStorage.setItem('currentEvent', JSON.stringify(partyEvent))
        this.selectedEvent = partyEvent;
    }

    clearCurrentEvent() {
        localStorage.removeItem('currentEvent')
        this.selectedEvent = null;
    }

    getRegistrationForm(eventId: Guid): Observable<ApiResponse<PartyEventRegistrationFormResponse>> {
        const orgId = this.getOrgId();
        const partyId = this.getPartyId();
        if (this.isOrgScope() || !partyId) {
            return this.get<ApiResponse<PartyEventRegistrationFormResponse>>(`v1/orgs/${orgId}/events/${eventId}/registration-form`);
        }
        return this.get<ApiResponse<PartyEventRegistrationFormResponse>>(`v1/orgs/${orgId}/parties/${partyId}/events/${eventId}/registration-form`);
    }

    getPublicRegistrationForm(eventId: Guid): Observable<ApiResponse<PartyEventRegistrationFormResponse>> {
        return this.get<ApiResponse<PartyEventRegistrationFormResponse>>(this.controller + `/public/${eventId}/registration-form`);
    }

    getPublicRegistrationPrices(eventId: Guid): Observable<SuccessList<ActivityRegistrationPriceResponse>> {
        return this.get<SuccessList<ActivityRegistrationPriceResponse>>(this.controller + `/public/${eventId}/registration-prices`);
    }

    updateRegistrationForm(eventId: Guid, formData: FormData): Observable<ApiResponse<PartyEventRegistrationFormResponse>> {
        const orgId = this.getOrgId();
        const partyId = this.getPartyId();
        if (this.isOrgScope() || !partyId) {
            return this.putMultiPart<ApiResponse<PartyEventRegistrationFormResponse>, FormData>(formData, `v1/orgs/${orgId}/events/${eventId}/registration-form`);
        }
        return this.putMultiPart<ApiResponse<PartyEventRegistrationFormResponse>, FormData>(formData, `v1/orgs/${orgId}/parties/${partyId}/events/${eventId}/registration-form`);
    }

    private getOrgId(): Guid {
        return this._storageService.getOrg()?.id;
    }

    private getPartyId(): Guid {
        return this._partySelectedService.getPartyId() ?? this._storageService.getParty()?.id;
    }

    private isOrgScope(): boolean {
        return this._storageService.getOrg()?.type === OrganizationType.Association;
    }

}
